import { Text } from '@user-portal/wikr-components';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { logout } from 'store/auth/actions';

import { EVENT_ACTION_LOCAL } from 'constants/eventAction';

import { sendAnalyticSideBarElementClick } from 'services/Analytics';
import { ActionEventAction } from 'services/Analytics/core';

import { useDispatch } from 'hooks';

import { PrimaryLinkList, SecondaryLinkList } from './components';
import { PRIMARY_SIDEBAR_LINKS, SECONDARY_SIDEBAR_LINKS } from './constants';
import { useShouldDisplayPersonalPlanLink, useSidebar } from './hooks';
import { useContactUsAction } from './hooks/useContactUsAction';

import * as S from './styled';

const Sidebar: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { isOpen, close, sidebarRef } = useSidebar();
    const { onClickContactUs } = useContactUsAction(close);

    const navigate = useNavigate();

    const shouldDisplayPersonalPlanLink = useShouldDisplayPersonalPlanLink();

    const handleLogout = () => {
        sendAnalyticSideBarElementClick(ActionEventAction.logOut);
        dispatch(logout());
        navigate('/');
        close();
    };

    const elementClickHandler = (eventAction: ActionEventAction) => {
        sendAnalyticSideBarElementClick(eventAction);
        close();
    };

    const filteredPrimaryLinks = useMemo(
        () =>
            PRIMARY_SIDEBAR_LINKS.filter(({ eventAction }) => {
                if (eventAction === EVENT_ACTION_LOCAL.PERSONAL_PLAN) {
                    return shouldDisplayPersonalPlanLink;
                }

                return true;
            }),
        [shouldDisplayPersonalPlanLink]
    );

    return (
        <>
            <S.SideBarOverlay
                data-testid="sidebar-overlay"
                isOpen={isOpen}
                onClick={() => elementClickHandler(ActionEventAction.close)}
            />
            <S.SidebarContainer data-locator="SidebarMenu" isOpen={isOpen} ref={sidebarRef}>
                <S.LinksContainer>
                    <PrimaryLinkList links={filteredPrimaryLinks} onLinkClick={elementClickHandler} />
                    <S.Divider />
                </S.LinksContainer>

                <S.LinksContainer>
                    <SecondaryLinkList links={SECONDARY_SIDEBAR_LINKS} onLinkClick={elementClickHandler} />

                    <S.SecondaryNavButton onClick={handleLogout} data-locator="burgerLogoutBtn">
                        <Text>{t('sidebar.logout')}</Text>
                    </S.SecondaryNavButton>
                    <S.Divider />
                </S.LinksContainer>

                <S.LinksContainer>
                    <S.SecondaryNavButton data-locator="burgerContactUs" onClick={onClickContactUs}>
                        <Text>{t('basics.contactUs')}</Text>
                    </S.SecondaryNavButton>
                </S.LinksContainer>
            </S.SidebarContainer>
        </>
    );
};

export default Sidebar;
